import {
    useCallback,
    useEffect,
    Fragment,
    useState,
    useMemo,
    useRef
} from "react";
import {
    ScrollView,
    Switch,
    View
} from "react-native";
import type SendMailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextAreaInput,
    SelectBox,
    TextInput,
    Dialog,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    GetAllUsersForAdminObjectType
} from "../../../../services/restAPI/actions/getAllUsersForAdmin/type";
import RESTServiceContext from "../../../../services/restAPI/context";

const SendMailModal = ({
    initialUserIds,
    isVisible,
    onSuccess,
    onClose
}: SendMailModalProps) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        subject,
        setSubject
    ] = useState<string>("");

    const [
        content,
        setContent
    ] = useState<string>("");

    const [
        sendToAll,
        setSendToAll
    ] = useState<boolean>(false);

    const [
        selectedUserIDs,
        setSelectedUserIDs
    ] = useState<string[]>([]);

    const [
        users,
        setUsers
    ] = useState<GetAllUsersForAdminObjectType[]>([]);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isConfirmDialogVisible,
        setIsConfirmDialogVisible
    ] = useState<boolean>(false);

    const selectBoxRef = useRef<React.ElementRef<typeof SelectBox>>(null);
    const prevUsersRef = useRef<GetAllUsersForAdminObjectType[]>([]);
    const lastFetchedSearchTextRef = useRef<string | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchUsers = useCallback((searchText?: string, isImmediate = false) => {
        if(searchText !== undefined && searchText === lastFetchedSearchTextRef.current) {
            return Promise.resolve();
        }
        lastFetchedSearchTextRef.current = searchText || "";

        if(searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        const runFetch = async (resolve: () => void) => {
            try {
                const requestParams: Record<string, unknown> = {
                    page: 1
                };
                if(searchText) {
                    requestParams.search = searchText;
                }

                const result = await RESTServiceContext.action("getAllUsersForAdmin", requestParams);
                if(result.payload) {
                    setUsers(result.payload);
                }
            } catch(error) {
                console.error(error);
            } finally {
                resolve();
            }
        };

        if(isImmediate) {
            return new Promise<void>((resolve) => {
                runFetch(resolve);
            });
        }

        return new Promise<void>((resolve) => {
            searchTimeoutRef.current = setTimeout(() => {
                runFetch(resolve);
            }, 300);
        });
    }, []);

    const selectBoxData = useMemo(() => {
        return users.map((user) => {
            const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
            const title = fullName ? (user.mail ? `${fullName} (${user.mail})` : fullName) : user.mail || user._id;

            return {
                __key: user._id,
                __title: title
            };
        });
    }, [
        users
    ]);

    const initialSelectedItems = useMemo(() => {
        return selectedUserIDs.map((id) => {
            const user = users.find((u) => u._id === id);
            return {
                __title: user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.mail || id : id,
                __key: id
            };
        });
    }, [
        selectedUserIDs,
        users
    ]);

    useEffect(() => {
        if(users !== prevUsersRef.current && selectBoxRef.current) {
            selectBoxRef.current.updateData(selectBoxData, initialSelectedItems);
            prevUsersRef.current = users;
        }
    }, [
        initialSelectedItems,
        selectBoxData,
        users
    ]);

    useEffect(() => {
        if(!isVisible) {
            setSubject("");
            setContent("");
            setSendToAll(false);
            setSelectedUserIDs(initialUserIds || []);
            setUsers([]);
            lastFetchedSearchTextRef.current = null;
            prevUsersRef.current = [];
        } else {
            lastFetchedSearchTextRef.current = null;
            fetchUsers("", true);
            if(initialUserIds && initialUserIds.length > 0) {
                setSelectedUserIDs(initialUserIds);
            }
        }
    }, [
        initialUserIds,
        fetchUsers,
        isVisible
    ]);

    const handleSendAction = async () => {
        if(subject.trim().length < 2) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-subject")
            });
            return;
        }

        if(content.trim().length < 5) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-content")
            });
            return;
        }

        if(!sendToAll && selectedUserIDs.length === 0) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-mail-user")
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const payload: {
                sendToAll: boolean;
                userIds?: string[];
                content: string;
                subject: string;
            } = {
                content: content.trim(),
                subject: subject.trim(),
                sendToAll: sendToAll
            };

            if(!sendToAll) {
                payload.userIds = selectedUserIDs;
            }

            const response = await RESTServiceContext.action("sendCustomMail", payload);

            if(response && !response.isError) {
                NCoreUIKitSnackBar.open({
                    title: localize("notifications-mail-success-sent")
                });
                onSuccess();
                onClose();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("notifications-mail-error-sent")
                });
            }
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("notifications-mail-error-sent")
            });
        } finally {
            setIsSubmitting(false);
            setIsConfirmDialogVisible(false);
        }
    };

    const handlePreSubmit = () => {
        if(subject.trim().length < 2) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-subject")
            });
            return;
        }

        if(content.trim().length < 5) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-content")
            });
            return;
        }

        if(!sendToAll && selectedUserIDs.length === 0) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-mail-user")
            });
            return;
        }

        if(sendToAll) {
            setIsConfirmDialogVisible(true);
            return;
        }

        handleSendAction();
    };

    return <Fragment>
        <Modal
            portalName="modal-system"
            id="send-mail-modal"
            isActive={isVisible}
            onClosed={onClose}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderRadius: radiuses.lg,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.headerContainer,
                        {
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.high}
                        variant="titleLargeSize"
                    >
                        {localize("notifications-new-mail")}
                    </Text>
                </View>
                <ScrollView
                    style={stylesheet.contentContainer}
                    showsVerticalScrollIndicator={true}
                >
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <TextInput
                            placeholder={localize("notifications-send-subject-placeholder")}
                            title={localize("notifications-send-subject-input")}
                            customColor={colors.project.inputBackground}
                            onChangeText={(val) => setSubject(val)}
                            spreadBehaviour="stretch"
                            value={subject}
                        />
                        <TextAreaInput
                            placeholder={localize("notifications-send-content-placeholder")}
                            key={`mail-content-input-${isVisible ? "visible" : "hidden"}`}
                            title={localize("notifications-send-content-input")}
                            customColor={colors.project.inputBackground}
                            onChangeText={(val) => setContent(val)}
                            isUpdateOnRealtime={true}
                            spreadBehaviour="stretch"
                            value={content}
                            minHeight={140}
                        />
                        <View
                            style={[
                                stylesheet.switchRowContainer,
                                {
                                    backgroundColor: colors.project.inputBackground,
                                    borderRadius: radiuses.sm,
                                    padding: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.switchTextContainer,
                                    {
                                        marginRight: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    customColor={colors.content.text.high}
                                    variant="bodyMediumSize"
                                >
                                    {localize("notifications-send-to-all-members")}
                                </Text>
                                <Text
                                    customColor={colors.content.text.mid}
                                    variant="bodySmallSize"
                                >
                                    {localize("notifications-send-mail-to-all-desc")}
                                </Text>
                            </View>
                            <Switch
                                onValueChange={(val) => setSendToAll(val)}
                                value={sendToAll}
                            />
                        </View>
                        {
                            !sendToAll ?
                                <View
                                    style={[
                                        stylesheet.selectBoxContainer,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <SelectBox
                                        onChange={(selectedItems) => {
                                            setSelectedUserIDs(selectedItems.map((item) => item.__key as string));
                                        }}
                                        titleExtractor={(item) => (item as { __title?: string }).__title || ""}
                                        keyExtractor={(item) => (item as { __key?: string }).__key || ""}
                                        onSearch={(props) => {
                                            props.setIsLoading(true);
                                            fetchUsers(props.searchText).finally(() => {
                                                props.setIsLoading(false);
                                            });

                                            if(!props.searchText) {
                                                return props.data;
                                            }

                                            const searchLower = props.searchText.toLowerCase();
                                            return props.data.filter((item) =>
                                                (item.__title || "").toLowerCase().includes(searchLower)
                                            );
                                        }}
                                        placeholder={localize("notifications-send-select-placeholder")}
                                        title={localize("notifications-send-select-users")}
                                        initialSelectedItems={initialSelectedItems}
                                        key="send-mail-users-selectbox"
                                        selectSheetProps={{
                                            portalName: "modal-system"
                                        }}
                                        isShowSelectSheetTools={true}
                                        spreadBehaviour="stretch"
                                        isMultipleSelect={true}
                                        data={selectBoxData}
                                        isSearchable={true}
                                        ref={selectBoxRef}
                                    />
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {`${localize("notifications-send-selected-count")} ${selectedUserIDs.length}`}
                                    </Text>
                                </View>
                            :
                                null
                        }
                    </View>
                </ScrollView>
                <View
                    style={[
                        stylesheet.footerContainer,
                        {
                            marginTop: spaces.spacingMd,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Button
                        title={localize("cancel")}
                        isDisabled={isSubmitting}
                        spreadBehaviour="free"
                        onPress={onClose}
                        variant="outline"
                        type="neutral"
                    />
                    <Button
                        title={localize("notifications-send-mail-submit")}
                        onPress={handlePreSubmit}
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                        spreadBehaviour="free"
                        variant="filled"
                        type="primary"
                    />
                </View>
            </View>
        </Modal>
        <Dialog
            content={localize("notifications-send-mail-confirm-content")}
            primaryButtonProps={{
                title: localize("notifications-send-confirm-yes"),
                onPress: handleSendAction
            }}
            secondaryButtonProps={{
                onPress: () => setIsConfirmDialogVisible(false),
                title: localize("cancel")
            }}
            isVisible={isConfirmDialogVisible}
            variant="yes-no"
        />
    </Fragment>;
};
export default SendMailModal;
